// src/app/providers/AntdConfigProvider.tsx
import React from 'react';
import { ConfigProvider } from 'antd';
import { StyleProvider } from '@ant-design/cssinjs';
import dayjs from 'dayjs';
import 'dayjs/locale/en';
import updateLocale from 'dayjs/plugin/updateLocale'; 
import localizedFormat from 'dayjs/plugin/localizedFormat';
import en_US from 'antd/locale/en_US';

dayjs.extend(localizedFormat);
dayjs.extend(updateLocale); // <--- NEW: Extend dayjs with updateLocale
dayjs.locale('en'); // <--- NEW: Set the global dayjs locale explicitly

import { useThemeStore } from '../store/themeStore';
import { useTranslation } from 'react-i18next';
import { buildRootCssVariables, THEME_SCHEME_KEYS, getSchemePalette } from '../theme/themeScheme';

type AntdConfigProviderProps = {
  children: React.ReactNode;
};

function AntdConfigProvider({ children }: AntdConfigProviderProps) {
  const { mode, activeScheme, themeConfig } = useThemeStore();
  const { i18n } = useTranslation();
    const direction = i18n.language === 'ar' ? 'rtl' : 'ltr';

  React.useEffect(() => {
    const root = document.documentElement;
    const variables = buildRootCssVariables(activeScheme, mode, themeConfig);
    Object.entries(variables).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });
    THEME_SCHEME_KEYS.forEach((schemeKey) => {
      root.style.setProperty(`--scheme-${schemeKey}-swatch`, getSchemePalette(schemeKey).primary);
    });
    root.style.colorScheme = mode;
  }, [activeScheme, mode, themeConfig]);

  return (
    <StyleProvider hashPriority="high">
      <ConfigProvider
        locale={en_US}
        direction={direction}
        theme={themeConfig}
      >
        {children}
      </ConfigProvider>
    </StyleProvider>
  );
}

export default AntdConfigProvider;